<template>
    <div class="card border-0 bg-white profile-settings-card rounded-0">
        <div class="card-body p-15 p-sm-20 p-md-25 p-lg-30 letter-spacing">
            <form v-on:submit="submit">
                <div class="row">
                    <!-- CAREGIVER INFO -->
                    <div class="col-12">
                        <h4 class="mb-15 mb-sm-20 mb-md-25">{{ $t('form.section.caregiver_information') }}</h4>
                    </div>
                    <!-- FIRST NAME -->
                    <div class="col-md-6">
                        <div class="form-group mb-15 mb-sm-20 mb-md-25">
                            <label class="d-block text-black fw-semibold mb-10">
                                {{ $t('form.first_name.label') }}
                            </label>
                            <input v-model="caregiver.firstname" required
                                type="text" :class="[{ 'is-invalid': submitted && errors?.firstname }]"
                                class="form-control shadow-none text-black"
                                :placeholder="$t('form.first_name.placeholder')" />
                            <!-- ERRORS -->
                            <div class="form-messages mt-2 px-1" v-if="errors?.firstname ?? false">
                                <p v-for="error in errors.firstname" :key="error"
                                    class="small text-danger lh-1 m-0 mt-1">{{
                                    error }}</p>
                            </div>
                        </div>
                    </div>
                    <!-- LAST NAME -->
                    <div class="col-md-6">
                        <div class="form-group mb-15 mb-sm-20 mb-md-25">
                            <label class="d-block text-black fw-semibold mb-10">
                                {{ $t('form.last_name.label') }}
                            </label>
                            <input v-model="caregiver.lastname" required
                                type="text" :class="[{ 'is-invalid': submitted && errors?.lastname }]"
                                class="form-control shadow-none text-black"
                                :placeholder="$t('form.last_name.placeholder')" />
                            <!-- ERRORS -->
                            <div class="form-messages mt-2 px-1" v-if="errors?.lastname ?? false">
                                <p v-for="error in errors.lastname" :key="error"
                                    class="small text-danger lh-1 m-0 mt-1">{{
                                    error }}</p>
                            </div>
                        </div>
                    </div>
                    <!-- EMAIL -->
                    <div class="col-md-6">
                        <div class="form-group mb-15 mb-sm-20 mb-md-25">
                            <label class="d-block text-black fw-semibold mb-10">
                                {{ $t('form.email.label') }}
                            </label>
                            <input v-model="caregiver.email" :disabled="update"
                                :class="[{ 'is-invalid': submitted && errors?.email }]"
                                pattern="[^@\s]+@[^@\s]+\.[^@\s]+" type="email"
                                class="form-control shadow-none text-black"
                                :placeholder="$t('form.email.placeholder')" />
                            <!-- ERRORS -->
                            <div class="form-messages mt-2 px-1" v-if="errors?.email ?? false">
                                <p v-for="error in errors.email" :key="error"
                                    class="small text-danger lh-1 m-0 mt-1">{{
                                    error }}</p>
                            </div>
                        </div>
                    </div>
                    <!-- PHONE -->
                    <div class="col-md-6">
                        <div class="form-group mb-15 mb-sm-20 mb-md-25">
                            <label class="d-block text-black fw-semibold mb-10">
                                {{ $t('form.phone.label') }}
                            </label>
                            <input v-model="caregiver.phone_number"
                                type="text" :class="[{ 'is-invalid': submitted && errors?.phone_number }]"
                                class="form-control shadow-none text-black"
                                :placeholder="$t('form.phone.placeholder')" />
                            <!-- ERRORS -->
                            <div class="form-messages mt-2 px-1" v-if="errors?.phone_number ?? false">
                                <p v-for="error in errors.phone_number" :key="error"
                                    class="small text-danger lh-1 m-0 mt-1">{{
                                    error }}</p>
                            </div>
                        </div>
                    </div>
                    <div class="col-12" v-if="loading">
                        <SpinnerLoading />
                    </div>
                    <div class="col-md-12" v-else>
                        <div class="d-flex align-items-center justify-content-between">
                            <!-- DELETE -->
                            <button v-if="update && destroyEnabled" type="button"
                                class="bg-transparent p-0 border-0 text-danger lh-1 fw-medium" data-bs-toggle="modal"
                                data-bs-target="#confirmModal">
                                <i class="ph ph-trash lh-1 me-1 position-relative top-2"></i>
                                <span class="position-relative">{{ $t('cta.delete_caregiver') }}</span>
                            </button>
                            <span v-else></span>
                            <!-- SAVE -->
                            <button
                                class="default-btn transition fw-medium text-white pt-10 pb-10 ps-25 pe-25 pt-md-11 pb-md-11 ps-md-35 pe-md-35 fs-md-15 fs-lg-16"
                                type="submit" v-on:click="submitted = true">
                                {{  update ? $t('cta.save_caregiver') : $t('cta.add_caregiver') }}
                            </button>
                        </div>
                    </div>
                </div>
            </form>
        </div>
    </div>
    <!-- ERROR MESSAGE -->
    <div class="row justify-content-center">
        <div class="col">
            <div class="alert alert-danger alert-dismissible fade border-0 border-danger-subtle border-top rounded-0 m-0 mb-25"
                role="alert" id="errorMessage">
                <strong>{{ $t('error.something_went_wrong') }}</strong>
                <button type="button" class="btn-close" v-on:click="hideError" aria-label="Close"></button>
            </div>
        </div>
    </div>
    <ConfirmModal @confirm="deleteCaregiver()" :title="$t('cta.$delete.title')" :message="$t('cta.$delete.message')" />
</template>

<script lang="ts">
import { defineComponent } from "vue";
import ConfirmModal from "@/components/Widgets/ConfirmModal.vue";
import { CreatePatientInfo, PatientInfo, Role, User, UserInfo } from "@/utils/models/user.model";
import { removeNullProperties } from "@/utils/methods/remove_null_properties";
import API from "@/services/api/api.service";
import stateStore from "@/utils/store";
import SpinnerLoading from "@/components/Widgets/Spinner.vue";

export default defineComponent({
    name: "CaregiverForm",
    components: {
        ConfirmModal,
        SpinnerLoading
    },
    data() {
        return {
            caregiver_id: this.$route.params.caregiver_id,
            caregiver: {} as UserInfo,
            patient_id: this.$route.params.id,
            patient: this.$route.meta.patient ?? {} as CreatePatientInfo,
            submitted: false,
            loading: false,
            errors: undefined as any,
        } as {
            caregiver_id?: string,
            caregiver: UserInfo,
            patient_id: string,
            patient?: PatientInfo,
            submitted: boolean,
            loading: boolean,
            errors?: any,
        }
    },
    mounted() {
        this.caregiver = this.patient?.users?.filter((user) => user.role.type === Role.caregiver && user.id === this.caregiver_id)[0] ?? {} as UserInfo;
    },
    computed: {
        update() {
            return this.$route.meta.patient !== undefined && this.$route.meta.patient !== null;
        },
        centers() {
            return stateStore.getCentersWithPermisison('store');
        },
        destroyEnabled(): boolean { return this.update ? stateStore.getCenterPermission(this.patient!.center_id, 'destroy') : false }
    },
    methods: {
        submit(event): void {
            this.errors = undefined;
            event.preventDefault();
            console.log("PATIENT", this.patient);

            this.loading = true;

            if (this.update) {
                console.log('UPDATE');
                removeNullProperties<UserInfo>(this.caregiver);
                API.updateCaregiver({patient_id: this.patient_id, caregiver: this.caregiver}).then((response) => {
                    this.$router.push(`/patient/${this.patient_id}/caregivers`);
                }).catch((error) => {
                    this.errors = error.response?.data?.errors;
                    this.showError();
                }).finally(() => {
                    this.loading = false
                });
            } else {
                API.addCaregiver({patient_id: this.patient_id, caregiver: this.caregiver}).then((response) => {
                    this.$router.push(`/patient/${this.patient_id}/caregivers`);
                }).catch((error) => {
                    this.errors = error.response?.data?.errors;
                    this.showError();
                }).finally(() => {
                    this.loading = false
                });
            }
        },
        deleteCaregiver(): void {
            API.deleteCaregiver({patient_id: this.patient_id!, caregiver_id: this.caregiver_id!}).then((response) => {
                if (response.data.success === true) {
                    this.$router.push(`/patients/${this.patient_id}/caregivers`);
                }
            }).catch((error) => {
                this.showError();
            }).finally(() => {
                this.loading = false;
                console.log("DELETED_CAREGIVER_ID", this.caregiver_id);
            })
        },
        showError() {
            const error = document.getElementById('errorMessage');
            error?.classList.add('show');
            setTimeout(() => {
                this.hideError();
            }, 5000)
        },
        hideError() {
            const error = document.getElementById('errorMessage');
            error?.classList.remove('show');
        }
    },
});
</script>